"use client"; // Modal are always rendered client-side

import { usePathname } from "next/navigation";
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ModalClient } from "@/src/components/ui/Modal/ModalClient";

interface ModalContextType {
  setModal: (modal: ReactNode) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: PropsWithChildren): ReactNode {
  const [modal, setModal] = useState<null | ReactNode>(null);
  const modalContext = useMemo(() => ({ setModal }), []);
  const pathname = usePathname();

  function closeModal(): void {
    setModal(null);
  }

  // Close modal on page change
  useEffect(closeModal, [pathname]);

  return (
    <ModalContext.Provider value={modalContext}>
      {children}
      <ModalClient onClose={closeModal}>{modal}</ModalClient>
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextType {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within ModalProvider");
  }
  return context;
}
